import { useEffect, useRef, useState } from "preact/hooks"
import type { User } from "../lib/types"

interface NavbarProps {
	user: User
	onLogout: () => void
}

// Users have no avatar (auth is email/password, not Google) — show initials
// in a small badge instead of an avatar image.
function initials(user: User): string {
	const source = (user.name?.trim() || user.email).trim()
	const parts = source.split(/[\s@.]+/).filter(Boolean)
	if (parts.length === 0) return "?"
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
	return (parts[0][0] + parts[1][0]).toUpperCase()
}

export function Navbar({ user, onLogout }: NavbarProps) {
	const [open, setOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)

	// Close the user menu on outside click.
	useEffect(() => {
		if (!open) return
		const onClick = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setOpen(false)
			}
		}
		document.addEventListener("click", onClick)
		return () => document.removeEventListener("click", onClick)
	}, [open])

	return (
		<div class="navbar navbar-expand border-bottom px-2 gap-2">
			<a
				class="d-inline-flex align-items-center text-decoration-none brand-name"
				href="/"
				style={{ color: "inherit" }}
				title="Artbox home"
			>
				<img
					src="/static/assets/logo.png"
					alt=""
					width="32"
					height="32"
					class="me-2"
				/>
				Artbox
			</a>
			<span class="flex-fill" />
			<div ref={containerRef} class="dropdown">
				<button
					type="button"
					class="btn btn-outline-secondary btn-sm dropdown-toggle d-inline-flex align-items-center gap-2"
					onClick={() => setOpen((v) => !v)}
					aria-expanded={open}
				>
					<span
						class="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary-emphasis fw-semibold flex-shrink-0"
						style={{ width: "20px", height: "20px", fontSize: "0.6rem" }}
						aria-hidden="true"
					>
						{initials(user)}
					</span>
					<span class="d-none d-sm-inline">{user.name || user.email}</span>
				</button>
				<ul
					class={`dropdown-menu dropdown-menu-end ${open ? "show" : ""}`}
					data-bs-popper="static"
					style={{ minWidth: "12rem", maxWidth: "calc(100vw - 2rem)" }}
				>
					<li>
						<span class="dropdown-item-text text-body-secondary small text-truncate d-block">
							{user.email}
						</span>
					</li>
					<li>
						<hr class="dropdown-divider" />
					</li>
					<li>
						<button
							type="button"
							class="dropdown-item d-flex align-items-center gap-2"
							onClick={() => {
								setOpen(false)
								onLogout()
							}}
						>
							<i class="bi bi-box-arrow-right" aria-hidden="true" />
							<span>Log out</span>
						</button>
					</li>
				</ul>
			</div>
		</div>
	)
}
